import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import MyVisits from "@/components/MyVisits";
import VisitDetailDialog from "@/components/VisitDetailDialog";
import VisitDialog from "@/components/VisitDialog";
import AddRestaurantDialog from "@/components/AddRestaurantDialog";
import RestaurantList from "@/components/RestaurantList";
import RestaurantFilters from "@/components/RestaurantFilters";
import ReviewSection from "@/components/ReviewSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Plus, ChefHat, Calendar as CalendarIcon, Globe } from "lucide-react";

const sortRestaurants = (list) => list.sort((a, b) => {
  if (a.country === 'Germany' && b.country !== 'Germany') return -1;
  if (b.country === 'Germany' && a.country !== 'Germany') return 1;
  const countryCompare = (a.country || '').localeCompare(b.country || '', 'de');
  if (countryCompare !== 0) return countryCompare;
  return (a.name || '').localeCompare(b.name || '', 'de');
});

const Restaurants = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [restaurants, setRestaurants] = useState([]);
  const [reviewRestaurant, setReviewRestaurant] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitedRestaurantIds, setVisitedRestaurantIds] = useState(new Set());
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [filters, setFilters] = useState({ country: "", stars: "", cuisine_type: "", price_range: "" });
  const [filterOptions, setFilterOptions] = useState({ countries: [], cuisines: [], stars: [1, 2, 3], price_ranges: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("all");
  const [selectedVisitDetail, setSelectedVisitDetail] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [totalStats, setTotalStats] = useState({ total: 0, stars3: 0, stars2: 0, stars1: 0 });

  // Check URL params for filter=visited
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('filter') === 'visited') {
      setViewMode("visits");
    }
  }, [location.search]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await axios.get(`${API}/restaurants/filters`, { withCredentials: true });
        setFilterOptions(response.data);
      } catch (error) {
        console.warn("Data load failed:", error);
      }
    };
    const loadWishlist = async () => {
      try {
        const response = await axios.get(`${API}/wishlist/ids`, { withCredentials: true });
        setWishlistIds(new Set(response.data));
      } catch (error) {
        console.warn("Data load failed:", error);
      }
    };
    const loadTotalStats = async () => {
      try {
        const response = await axios.get(`${API}/restaurants`, { withCredentials: true });
        const all = response.data;
        setTotalStats({
          total: all.length,
          stars3: all.filter(r => r.stars === 3).length,
          stars2: all.filter(r => r.stars === 2).length,
          stars1: all.filter(r => r.stars === 1).length
        });
      } catch (error) {
        console.warn("Data load failed:", error);
      }
    };
    loadFilters();
    loadWishlist();
    loadTotalStats();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (filters.country) params.append("country", filters.country);
        if (filters.stars) params.append("stars", filters.stars);
        if (filters.cuisine_type) params.append("cuisine_type", filters.cuisine_type);
        if (filters.price_range) params.append("price_range", filters.price_range);

        const [restaurantsRes, visitsRes] = await Promise.all([
          axios.get(`${API}/restaurants?${params}`, { withCredentials: true }),
          axios.get(`${API}/visits`, { withCredentials: true })
        ]);

        setRestaurants(sortRestaurants(restaurantsRes.data));
        setVisits(visitsRes.data);
        setVisitedRestaurantIds(new Set(visitsRes.data.map(v => v.restaurant_id)));
      } catch (error) {
        console.warn("Restaurants/visits load failed:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [search, filters, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const toggleWishlist = async (restaurantId, e) => {
    e.stopPropagation();
    try {
      if (wishlistIds.has(restaurantId)) {
        await axios.delete(`${API}/wishlist/${restaurantId}`, { withCredentials: true });
        setWishlistIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(restaurantId);
          return newSet;
        });
        toast.success(t('wishlist.removed'));
      } else {
        await axios.post(`${API}/wishlist/${restaurantId}`, {}, { withCredentials: true });
        setWishlistIds(prev => new Set([...prev, restaurantId]));
        toast.success(t('wishlist.added'));
      }
    } catch (error) {
      toast.error("Failed to update wishlist");
    }
  };

  const unvisitedRestaurants = restaurants.filter(r => !visitedRestaurantIds.has(r.restaurant_id));

  return (
    <div className="min-h-screen bg-background" data-testid="restaurants-page">
      <Navigation currentPage="restaurants" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{t('restaurants.title')}</h1>
            <p className="text-muted-foreground">{t('restaurants.subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowAddRestaurant(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="add-restaurant-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('restaurants.addUnlisted')}
          </Button>
        </div>

        {/* Restaurant Statistics */}
        <Card className="bg-card/50 border-border p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold gradient-text">{totalStats.total.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Restaurants</p>
              </div>
              <div className="h-8 w-px bg-muted/50" />
              <div className="flex gap-4">
                {[3, 2, 1].map(stars => {
                  const count = { 3: totalStats.stars3, 2: totalStats.stars2, 1: totalStats.stars1 }[stars];
                  return (
                    <button
                      key={`stat-${stars}`}
                      onClick={() => setFilters({ ...filters, stars: filters.stars === String(stars) ? "" : String(stars) })}
                      className={`text-center px-3 py-1 rounded-lg transition-colors ${
                        filters.stars === String(stars) ? 'bg-primary/20 border border-primary/40' : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-1 justify-center">
                        {[...Array(stars)].map((_, i) => (
                          <Star key={`stat-star-${stars}-${i}`} className="w-3 h-3 text-primary fill-primary" />
                        ))}
                      </div>
                      <p className="text-sm font-semibold">{count}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{filterOptions.countries.length} Länder</span>
            </div>
          </div>
        </Card>

        <RestaurantFilters
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
        />

        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Button
              variant={viewMode === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("all")}
              className={viewMode === "all" ? "bg-primary text-primary-foreground" : "border-border"}
              data-testid="tab-all-restaurants"
            >
              <ChefHat className="w-4 h-4 mr-2" />
              {t('restaurants.michelinRestaurants')} ({unvisitedRestaurants.length})
            </Button>
            <Button
              variant={viewMode === "visits" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("visits")}
              className={viewMode === "visits" ? "bg-primary text-primary-foreground" : "border-border"}
              data-testid="tab-my-visits"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Meine Besuche ({visits.length})
            </Button>
          </div>

          {viewMode === "visits" ? (
            <>
              {search && restaurants.length > 0 &&
                !visits.some(v => (v.restaurant_name || "").toLowerCase().includes(search.toLowerCase())) && (
                <button
                  onClick={() => setViewMode("all")}
                  className="w-full mb-4 text-sm text-primary border border-primary/30 bg-primary/5 rounded-lg p-3 hover:bg-primary/10 transition-colors text-left"
                  data-testid="switch-to-database-hint"
                >
                  Kein Besuch passt zu „{search}" – aber es gibt {restaurants.length} Treffer in der Restaurant-Datenbank. Hier klicken, um sie anzuzeigen.
                </button>
              )}
              <MyVisits visits={visits} search={search} onSelect={setSelectedVisitDetail} />
            </>
          ) : (
            <RestaurantList
              restaurants={unvisitedRestaurants}
              wishlistIds={wishlistIds}
              loading={loading}
              onSelect={setSelectedRestaurant}
              onToggleWishlist={toggleWishlist}
              onShowReviews={setReviewRestaurant}
            />
          )}
        </div>
      </main>

      <AddRestaurantDialog open={showAddRestaurant} onOpenChange={setShowAddRestaurant} />

      <VisitDialog
        open={!!selectedRestaurant}
        restaurant={selectedRestaurant}
        onClose={() => setSelectedRestaurant(null)}
        onSuccess={() => { setSelectedRestaurant(null); reload(); }}
      />

      <VisitDetailDialog
        open={!!selectedVisitDetail}
        onOpenChange={(isOpen) => { if (!isOpen) setSelectedVisitDetail(null); }}
        visit={selectedVisitDetail}
        onUpdated={reload}
      />

      <Dialog open={!!reviewRestaurant} onOpenChange={(isOpen) => { if (!isOpen) setReviewRestaurant(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reviews-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-primary fill-primary" />
              {reviewRestaurant?.name}
            </DialogTitle>
          </DialogHeader>
          {reviewRestaurant && <ReviewSection restaurant={reviewRestaurant} userId={user?.user_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Restaurants;
